import _ from 'lodash';
import moment from 'moment';
import React, { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import styled from 'styled-components';
import { ApprovalType, ApprovalWithAssociatedEntity } from 'api/home';
import { Avatar } from 'components/atoms/avatar';
import {
  CpBody00Primary,
  CpBody00Secondary,
  CpBody01Secondary,
  CpBody02Secondary,
} from 'components/atoms/customer-portal/typography';
import { RoutePaths } from 'constant';
import { TaskDrawerTab } from 'hooks/use-navigate-to-task';
import { useTaskDrawerQueryParamsFromRouter } from 'hooks/use-query-params';
import { useHomeQuery } from 'queries';
import { fullName } from 'utils/full-name';
import { TryGetTextFromHtml } from 'utils/getTextFromHTML';
import { useCustomerPortalHashMap } from '../../utils';

const Wrapper = styled.div`
  width: 100%;
  overflow-y: auto;
  padding-bottom: var(--cp-spacing-07);
  display: flex;
  flex-direction: column;
  height: calc(518px - 53px - 46px);
`;

const ApprovalWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: var(--cp-spacing-05);
  border-bottom: 1px solid var(--cp-gray-10);
  cursor: pointer;
  &:hover {
    background-color: var(--cp-gray-10);
  }
`;

const ApprovalNameAndDateWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: var(--cp-spacing-03);
`;

const ApprovalNameWrapper = styled.div`
  display: flex;
  align-items: start;
  width: 100%;
`;

const ApprovalName = styled(CpBody00Primary)`
  .rl-cp-approval-approver-name {
    margin-left: var(--cp-spacing-03);
  }
`;

const ApprovalDateWrapper = styled.div`
  display: flex;
  align-items: end;
  justify-content: flex-end;
  width: 100%;
`;

const ApprovalDate = styled(CpBody00Primary)<{ isDelayed?: boolean }>`
  color: ${(props) =>
    props.isDelayed ? 'var(--cp-red-50)' : 'var(--cp-green-50)'};
`;

const ApprovalMessageWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
  margin-top: var(--cp-spacing-03);
  margin-bottom: var(--cp-spacing-03);
`;

const ApprovalMessage = styled(CpBody00Primary)``;

const ApprovalResourceBreakCrumbsWrapper = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  flex-wrap: wrap;
`;

const ApprovalResourceBreakCrumbs = styled(CpBody00Secondary)``;

const CpApprovalAssociatedEntity: React.FC<{
  approval: ApprovalWithAssociatedEntity;
}> = ({ approval }) => {
  if (approval?.associatedEntityType === ApprovalType.TaskApproval) {
    return (
      <>
        {[
          approval?.associatedEntity?.project?.projectName,
          approval?.associatedEntity?.parentTaskObject?.taskName,
          approval?.associatedEntity?.taskName,
        ]
          .filter(Boolean)
          .join(' > ')}
      </>
    );
  }

  if (approval?.associatedEntityType === ApprovalType.SpaceTabApproval) {
    return (
      <>
        {[
          approval?.associatedEntity?.space?.project?.projectName,
          approval?.associatedEntity?.space?.spaceName,
          approval?.associatedEntity?.spaceTabTitle,
        ]
          .filter(Boolean)
          .join(' > ')}
      </>
    );
  }

  return <></>;
};

const CpApproval: React.FC<{
  approval: ApprovalWithAssociatedEntity;
  onClick: (a: ApprovalWithAssociatedEntity) => void;
}> = ({ approval, onClick }) => {
  return (
    <ApprovalWrapper
      onClick={() => {
        onClick(approval);
      }}
    >
      <ApprovalNameAndDateWrapper>
        <ApprovalNameWrapper>
          <ApprovalName>
            <Avatar
              src={approval?.requestBy?.photoUrl}
              alt={fullName(approval?.requestBy)}
              name={fullName(approval?.requestBy)}
            />
            <span className="rl-cp-approval-approver-name">
              {fullName(approval?.requestBy)}
            </span>
          </ApprovalName>
        </ApprovalNameWrapper>
        <ApprovalDateWrapper>
          <ApprovalDate
            isDelayed={moment(approval?.createdAt).isBefore(
              moment().subtract(1, 'days'),
            )}
          >
            {moment(approval?.createdAt)?.format('DD MMM YY')}
          </ApprovalDate>
        </ApprovalDateWrapper>
      </ApprovalNameAndDateWrapper>
      <ApprovalMessageWrapper>
        <ApprovalMessage>
          <TryGetTextFromHtml text={approval?.comments?.at(0)?.content ?? ''} />
        </ApprovalMessage>
      </ApprovalMessageWrapper>
      <ApprovalResourceBreakCrumbsWrapper>
        <ApprovalResourceBreakCrumbs>
          <CpApprovalAssociatedEntity approval={approval} />
        </ApprovalResourceBreakCrumbs>
      </ApprovalResourceBreakCrumbsWrapper>
    </ApprovalWrapper>
  );
};

const EmptyStateWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: calc(518px - 53px - 46px);
  flex-direction: column;

  padding-left: var(--cp-spacing-06);
  padding-right: var(--cp-spacing-06);
  p {
    text-align: center;
    margin-bottom: var(--cp-spacing-02);
  }
`;

export const CpApprovalsTab: React.FC<{
  onVisiblityChange: (isVisible: boolean) => void;
}> = ({ onVisiblityChange }) => {
  const { data: homeData } = useHomeQuery();
  const { hashMap } = useCustomerPortalHashMap();
  const { openTaskDrawer } = useTaskDrawerQueryParamsFromRouter();
  const router = useHistory();

  const onApprovalClick = (a: ApprovalWithAssociatedEntity) => {
    if (a?.associatedEntityType === ApprovalType.TaskApproval) {
      const taskId = a?.associatedEntity?.taskId;
      const projectId = a?.associatedEntity?.project?.projectId;

      if (taskId && projectId) {
        openTaskDrawer(projectId, taskId, TaskDrawerTab.Approvals);
      }
    } else if (a?.associatedEntityType === ApprovalType.SpaceTabApproval) {
      const spaceTabId = a?.associatedEntity?.spaceTabId;
      const projectId = a?.associatedEntity?.space?.project?.projectId;
      const spaceId = a?.associatedEntity?.space?.spaceId;

      if (spaceTabId && projectId && spaceId) {
        router.push({
          pathname: RoutePaths.customerPortalSpaceTabById
            .replace(':projectId', projectId.toString())
            .replace(':spaceId', spaceId.toString())
            .replace(':spaceTabId', spaceTabId.toString()),
          hash: `#ppi=${hashMap?.ppi ?? projectId}&st_approvals=open`,
        });
      }
    }
    onVisiblityChange(false);
  };
  const approvals = useMemo(
    () => _.sortBy(homeData?.approvals, (a) => a?.createdAt),
    [homeData?.approvals],
  );

  return (
    <Wrapper>
      {approvals?.length === 0 ? (
        <EmptyStateWrapper>
          <CpBody02Secondary>
            <FormattedMessage id="cp.approvals_empty_state.title" />
          </CpBody02Secondary>
          <CpBody01Secondary>
            <FormattedMessage id="cp.approvals_empty_state.description" />
          </CpBody01Secondary>
        </EmptyStateWrapper>
      ) : (
        approvals?.map((approval) => (
          <CpApproval
            onClick={onApprovalClick}
            key={approval.approvalId}
            approval={approval}
          />
        ))
      )}
    </Wrapper>
  );
};
